import React, { useState } from 'react';
import { ImagePlaceholder, PrimaryButton } from '../ui/Primitives.jsx';
import { resolveImage, fillTemplate } from '../../lib/helpers.js';
import PaywallIntl from './PaywallIntl.jsx';

// ============================================================================
// Paywall principal — agora SEMPRE mostra a tela longa de rolagem única
// (PaywallIntl.jsx), em todos os mercados incluindo o BR (mudança de
// 02/10/2026, pedido explícito da Gladis: "tira fora as telas de pagamento
// que estão ali no Brasil, não vamos usar essas, vamos usar a nova...
// quero igual o que te passei do Befit, até porque é outro funil").
// PaywallIntl já sabe diferenciar BR (1 plano semanal real do Stripe) dos
// outros 7 mercados (3 planos placeholder) — ver comentário lá.
//
// O card único antigo (PaywallBR, abaixo) fica mantido mas SEM USO, só pra
// não perder o código caso precise reverter — não é mais chamado por nada.
// ============================================================================
export default function Paywall(props) {
  return <PaywallIntl {...props} />;
}

// ----------------------------------------------------------------------------
// [DESATIVADA em 02/10/2026 — ver comentário acima] Paywall BR antiga —
// 1 plano semanal, card único.
//
// DIFERENCIAL VS. CONCORRÊNCIA: a política de cancelamento/cobrança fica no MESMO
// tamanho de fonte do resto do texto, sempre visível — nunca em rodapé
// minúsculo. E existe SEMPRE a opção de "pausar" a assinatura, não só
// cancelar (ver App_Fitness_Especificacao.md, seção de retenção/LTV).
//
// PREÇOS: todo campo vindo de step.plans com "GLADIS: DEFINA AQUI" no
// config é decisão de negócio da Gladis — aqui só a estrutura funcional
// está pronta pra receber os valores reais.
// ----------------------------------------------------------------------------
// eslint-disable-next-line no-unused-vars
function PaywallBR({ step, gender, onContinue, onClose }) {
  // Hoje só existe 1 plano real (semanal, ver quizConfig.js) — mantém o
  // state por compatibilidade caso um dia a gente adicione mais opções
  // (ex: se a Gladis decidir replicar a tela de 3 planos que o Befit
  // também usa no Brasil — ver pesquisa-concorrentes.md).
  const [selectedPlan] = useState(
    step.plans.find((p) => p.highlight)?.id ?? step.plans[0].id
  );
  const plan = step.plans.find((p) => p.id === selectedPlan);
  const images = step.backgroundImages?.[gender] ?? [];

  return (
    <div className="phone-frame flex flex-col safe-top safe-bottom">
      <div className="relative h-44 -mx-0">
        <div className="grid grid-cols-3 h-full">
          {(images.length ? images : [null, null, null]).map((img, i) => (
            <ImagePlaceholder key={i} src={img} className="w-full h-full object-cover" label="" />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-bg/10 to-bg" />
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
          <button className="text-xs bg-bg/60 backdrop-blur px-3 py-1.5 rounded-full">Restaurar compra</button>
          <button onClick={onClose} aria-label="Fechar" className="w-8 h-8 rounded-full bg-bg/60 backdrop-blur flex items-center justify-center">
            ✕
          </button>
        </div>
      </div>

      <div className="px-5 -mt-6 relative flex-1 flex flex-col">
        <h1 className="font-display text-2xl font-extrabold mb-4">{step.headline}</h1>

        <ul className="flex flex-col gap-2 mb-6">
          {step.benefits.map((b) => (
            <li key={b} className="flex items-center gap-2 text-sm">
              <span className="text-cyan">✓</span> {b}
            </li>
          ))}
        </ul>

        {/* Ancoragem de preço estilo Befit (ver pesquisa-concorrentes.md):
            preço cheio riscado + desconto + total baixo em destaque +
            valor recorrente real em letra do MESMO tamanho do resto
            (nunca escondido, ver regra permanente do projeto sobre
            cobrança nunca ficar em rodapé minúsculo). */}
        <div className="mb-6">
          {plan?.discountBadge && (
            <div className="text-center mb-2">
              <span className="inline-block text-[11px] bg-cyan text-bg px-3 py-1 rounded-full font-bold">
                {plan.discountBadge}
              </span>
            </div>
          )}
          <div className="rounded-xl2 border border-cyan bg-surface-2 p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-display font-bold text-sm">Teste de 1 Semana</span>
              {plan?.trialDays ? (
                <span className="text-[10px] text-ink-muted">Renova em {plan.trialDays} dias</span>
              ) : null}
            </div>
            <div className="flex items-end justify-between">
              <div className="flex items-center gap-2">
                <span className="text-ink-muted line-through text-sm">{plan?.priceFullLabel}</span>
                <span className="font-display text-2xl font-extrabold text-cyan">{plan?.priceEntryLabel}</span>
              </div>
              <span className="text-[11px] text-ink-muted">total hoje</span>
            </div>
          </div>
        </div>

        <div className="flex-1" />

        <div className="py-4">
          <PrimaryButton onClick={() => onContinue(plan)}>{step.cta}</PrimaryButton>

          {/* Mesma hierarquia visual do preço — sem letra miúda escondendo cobrança/cancelamento */}
          <p className="text-center text-sm text-ink mt-4">
            {fillTemplate(step.billingDisclosureTemplate, {
              precoTotal: plan?.priceEntryLabel ?? plan?.priceTotalLabel ?? 'R$ 0,00',
              precoCheio: plan?.priceFullLabel ?? plan?.priceTotalLabel ?? 'R$ 0,00',
              periodo: plan?.periodLabel ?? (plan?.months === 1 ? 'mês' : plan?.months === 12 ? 'ano' : `${plan?.months} meses`),
            })}
          </p>

          <button className="w-full text-center text-cyan text-sm font-semibold mt-4">{step.promoCodeLink}</button>

          <div className="flex justify-center gap-4 mt-4 text-xs text-ink-muted">
            {step.footerLinks.map((l) => (
              <span key={l}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
